<app-confirm-dialog
  data-testid="om-entry-dialog"
  [visible]="true"
  [wide]="true"
  [icon]="creating() ? 'add' : 'edit'"
  confirmColor="primary"
  [title]="
    creating()
      ? ('objectMappings.entryDialog.titleCreate' | translate)
      : ('objectMappings.entryDialog.titleEdit' | translate: { name: entry()?.logicalName })
  "
  [message]="'objectMappings.entryDialog.message' | translate"
  [confirmLabel]="
    (creating() ? 'objectMappings.entryDialog.submitCreate' : 'objectMappings.entryDialog.submitEdit') | translate
  "
  [cancelLabel]="'objectMappings.entryDialog.cancel' | translate"
  [busy]="saving()"
  [confirmDisabled]="!canSave()"
  (confirmed)="save()"
  (cancelled)="cancelled.emit()"
>
  <form class="oed-form" data-testid="om-entry-form" (submit)="$event.preventDefault(); save()">
    <div class="oed-fields">
      @if (creating()) {
        <mat-form-field appearance="fill" floatLabel="always" class="oed-field tc-field tc-field--uppercase-label">
          <mat-label>{{ 'objectMappings.entryDialog.name' | translate }}</mat-label>
          <input
            #nameInput
            matInput
            required
            data-testid="om-entry-name"
            autocomplete="off"
            [value]="name()"
            [attr.maxlength]="nameMax"
            [attr.aria-describedby]="showInvalidName() ? 'om-entry-invalid-name' : null"
            (input)="onName($event)"
          />
          <mat-hint>{{ 'objectMappings.entryDialog.nameHint' | translate }}</mat-hint>
        </mat-form-field>
      } @else {
        <div class="oed-readonly">
          <span class="oed-label">{{ 'objectMappings.entryDialog.name' | translate }}</span>
          <span class="oed-readonly-value" data-testid="om-entry-name-readonly">{{ entry()?.logicalName }}</span>
        </div>
      }

      <mat-form-field appearance="fill" floatLabel="always" class="oed-field tc-field tc-field--uppercase-label">
        <mat-label>{{ 'objectMappings.entryDialog.type' | translate }}</mat-label>
        <select matNativeControl data-testid="om-entry-type" [required]="creating()" (change)="onType($event)">
          <option value="" [selected]="!typeName()">
            {{
              (creating() ? 'objectMappings.entryDialog.typePlaceholder' : 'objectMappings.entryDialog.noType')
                | translate
            }}
          </option>
          @for (type of typeOptions(); track type) {
            <option [value]="type" [selected]="type === typeName()">{{ type }}</option>
          }
        </select>
      </mat-form-field>
    </div>
    @if (showInvalidName()) {
      <p id="om-entry-invalid-name" class="oed-hint oed-hint--error" data-testid="om-entry-invalid-name">
        {{ 'objectMappings.entryDialog.invalidName' | translate }}
      </p>
    }

    <mat-form-field appearance="fill" floatLabel="always" class="oed-field tc-field tc-field--uppercase-label">
      <mat-label>{{ 'objectMappings.entryDialog.url' | translate }}</mat-label>
      <input
        matInput
        data-testid="om-entry-url"
        autocomplete="off"
        [value]="url()"
        [attr.maxlength]="textMax"
        (input)="onUrl($event)"
      />
      <mat-hint>{{ 'objectMappings.entryDialog.urlHint' | translate }}</mat-hint>
    </mat-form-field>

    <fieldset class="oed-candidates">
      <legend class="oed-label">{{ 'objectMappings.entryDialog.candidates' | translate }}</legend>
      <p class="oed-hint">{{ 'objectMappings.entryDialog.candidatesHint' | translate }}</p>
      <ol class="oed-list">
        @for (row of rows(); track row.key; let i = $index, first = $first, last = $last) {
          <li class="oed-row" data-testid="om-locator-row">
            <span class="oed-rank" aria-hidden="true">{{ i + 1 }}</span>
            <mat-form-field appearance="fill" subscriptSizing="dynamic" class="oed-strategy tc-field">
              <mat-label>{{ 'objectMappings.entryDialog.strategy' | translate }}</mat-label>
              <select matNativeControl data-testid="om-locator-strategy" (change)="onStrategy(i, $event)">
                @for (strategy of strategyOptions(row.strategy); track strategy) {
                  <option [value]="strategy" [selected]="strategy === row.strategy">
                    {{
                      isKnownStrategy(strategy)
                        ? ('objectMappings.entryDialog.strategies.' + strategy | translate)
                        : strategy
                    }}
                  </option>
                }
              </select>
            </mat-form-field>
            <mat-form-field appearance="fill" subscriptSizing="dynamic" class="oed-value tc-field">
              <mat-label>{{ 'objectMappings.entryDialog.value' | translate }}</mat-label>
              <input
                matInput
                required
                class="oed-mono"
                data-testid="om-locator-value"
                autocomplete="off"
                [value]="row.value"
                [attr.maxlength]="textMax"
                (input)="onValue(i, $event)"
              />
            </mat-form-field>
            @if (isRole(row)) {
              <mat-form-field appearance="fill" subscriptSizing="dynamic" class="oed-role-name tc-field">
                <mat-label>{{ 'objectMappings.entryDialog.locatorName' | translate }}</mat-label>
                <input
                  matInput
                  data-testid="om-locator-name"
                  autocomplete="off"
                  [value]="row.name"
                  [attr.maxlength]="nameMax"
                  (input)="onLocatorName(i, $event)"
                />
              </mat-form-field>
            }
            <div class="oed-row-actions">
              <button
                type="button"
                class="oed-action"
                data-testid="om-locator-up"
                [disabled]="first"
                [attr.aria-label]="'objectMappings.entryDialog.moveUp' | translate: { position: i + 1 }"
                (click)="move(i, -1)"
              >
                <mat-icon aria-hidden="true">arrow_upward</mat-icon>
              </button>
              <button
                type="button"
                class="oed-action"
                data-testid="om-locator-down"
                [disabled]="last"
                [attr.aria-label]="'objectMappings.entryDialog.moveDown' | translate: { position: i + 1 }"
                (click)="move(i, 1)"
              >
                <mat-icon aria-hidden="true">arrow_downward</mat-icon>
              </button>
              <button
                type="button"
                class="oed-action oed-action--danger"
                data-testid="om-locator-remove"
                [attr.aria-label]="'objectMappings.entryDialog.remove' | translate: { position: i + 1 }"
                (click)="removeRow(i)"
              >
                <mat-icon aria-hidden="true">delete</mat-icon>
              </button>
            </div>
          </li>
        }
      </ol>
      @if (fromFulltag()) {
        <p class="oed-hint" data-testid="om-locator-fulltag">
          {{ 'objectMappings.entryDialog.fromFulltag' | translate }}
        </p>
      } @else if (noCandidates()) {
        <p class="oed-hint oed-hint--error" data-testid="om-locator-empty">
          {{ 'objectMappings.entryDialog.candidatesRequired' | translate }}
        </p>
      }
      @if (showValueMissing()) {
        <p class="oed-hint oed-hint--error" data-testid="om-locator-value-missing">
          {{ 'objectMappings.entryDialog.valueRequired' | translate }}
        </p>
      }
      @if (tooLong()) {
        <p class="oed-hint oed-hint--error" data-testid="om-locator-too-long">
          {{ 'objectMappings.entryDialog.tooLong' | translate }}
        </p>
      }
      <div class="oed-add">
        <button
          mat-stroked-button
          type="button"
          data-testid="om-locator-add"
          [disabled]="rows().length >= maxCandidates"
          (click)="addRow()"
        >
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'objectMappings.entryDialog.add' | translate }}
        </button>
        @if (rows().length >= maxCandidates) {
          <span class="oed-hint" data-testid="om-locator-max">
            {{ 'objectMappings.entryDialog.maxReached' | translate: { max: maxCandidates } }}
          </span>
        }
      </div>
    </fieldset>
  </form>
  @if (error(); as message) {
    <p class="oed-hint oed-hint--error" role="alert" data-testid="om-entry-error">{{ message }}</p>
  }
</app-confirm-dialog>
